import { PDFViewer, pdf } from '@react-pdf/renderer';
import { Dispatch, SetStateAction, useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';

import { META_COLLECT_API } from '../../../../../../api';
import { ModalInfoTemplete } from '../../../../../../components/Modal/ModalInfoTemplete';
import { ContainerItemModal } from '../../../../../../components/Modal/ModalInfoTemplete/styles';
import { PageHeader, ViewMode } from '../../../../../../components/PageHeader';
import { useSigninContext } from '../../../../../../context/AuthProviderPages/Signin';
import {
  ContainerPage,
  PageContent,
} from '../../../../../../styles/global.styles';
import { PQFLVisitRow } from '../../page';
import { PQFLDocument } from '../pdf';
import { formatISODate } from '../table';
import * as S from './styled';
import { processImage, getAnswerValue } from './utils';

type ContentWrapperProps = {
  loading: boolean;
  row: PQFLVisitRow | null;
  openModal: boolean;
  handleCloseModal: () => void;
  children: React.ReactNode;
  modifyViewMode?: ViewMode;
  modalType: 'visualize' | 'print' | null;
  setModifyViewMode?: Dispatch<SetStateAction<ViewMode>>;
};

type QualityItem = {
  DFSIGLAANALISE: string;
  DFMEDIA: number;
  DFMEDIATRIMESTRE: number;
  DFUNIDADE?: string;
};

export const ContentWrapper = ({
  loading,
  row: question,
  openModal,
  handleCloseModal,
  children,
  modifyViewMode,
  modalType,
  setModifyViewMode,
}: ContentWrapperProps) => {
  const { user, path } = useSigninContext();
  // estados para monitorar o carregamento da logo, da assinatura do produtor, da assinatura do técnico e dos dados de qualidade, respectivamente
  const [finalLogo, setFinalLogo] = useState<string | undefined>(undefined);
  const [finalSignature, setFinalSignature] = useState<string | undefined>(
    undefined,
  );
  const [finalTechSignature, setFinalTechSignature] = useState<
    string | undefined
  >(undefined);
  const [qualityData, setQualityData] = useState<QualityItem[]>([]);

  // estado que monitora a abertura do modal e dispara a busca dos dados na api
  useEffect(() => {
    const fetchQuality = async () => {
      if (openModal && question?.propriedade?.id && question?.data_final) {
        try {
          const { data: response } = await META_COLLECT_API.get(
            `/pqfl/qualidade/visita-qualidade`,
            {
              params: {
                idPropriedade: question.propriedade.id,
                dataFinal: question.data_final,
              },
              headers: { path },
            },
          );
          const formatted = (response.data || []).map((item: QualityItem) => ({
            ...item,
            DFMEDIA: item.DFMEDIA
              ? String(item.DFMEDIA).substring(0, 5)
              : item.DFMEDIA,
          }));
          setQualityData(formatted);
        } catch (error) {
          toast.error('Erro ao buscar dados da qualidade para o PDF');
        }
      }
    };
    fetchQuality();
  }, [openModal, question?.id, path]);

  useEffect(() => {
    if (openModal && question?.id) {
      if (modalType === 'print') {
        processImage(user?.DFLOGOEMPRESA).then(setFinalLogo);
      }
      processImage(question.assinatura_produtor).then(setFinalSignature);
      processImage(question.assinatura_tecnico).then(setFinalTechSignature);
    }
  }, [
    modalType,
    openModal,
    question?.id,
    user?.DFLOGOEMPRESA,
    question?.assinatura_produtor,
    question?.assinatura_tecnico,
  ]);

  const handleOpenNewTab = async () => {
    if (!question) return;
    const doc = (
      <PQFLDocument
        base64Data={finalLogo}
        assinatura_produtor={finalSignature}
        assinatura_tecnico={finalTechSignature}
        username={user?.DFLOGINUSUARIO}
        company={{ legalName: question.unidade.nome }}
        answers={question.respostas.filter(r => r.status !== 'C')}
        qualityItems={qualityData}
        property={{
          name: question.propriedade.nome_propriedade,
          producerName: question.propriedade.nome_produtor,
          municipalityName: question.propriedade.nome_municipio,
        }}
        technicalRecommendation={question.recomendacao_tecnica}
        endDate={question.data_final}
      />
    );
    const blob = await pdf(doc).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  return (
    <>
      <PageHeader
        pageIcon={{ option: 'pqfl_visitas' }}
        pageTitle="PQFL / Visitas"
        hasFilter={false}
        modifyViewMode={modifyViewMode}
        setModifyViewMode={setModifyViewMode}
      />
      <ContainerPage>
        <PageContent
          style={{
            justifyContent: loading === true ? 'center' : 'space-between',
          }}
        >
          {children}
        </PageContent>
        {openModal && question && (
          <>
            {/* MODAL VISUALIZAR */}
            {modalType === 'visualize' && (
              <ModalInfoTemplete
                option={'pqfl_visitas'}
                titleModal={'Visita PQFL'}
                closeModal={handleCloseModal}
                isOpen={openModal}
              >
                <S.RowItens>
                  <ContainerItemModal>
                    <label>Código</label>
                    <div>
                      <span>{question.id}</span>
                    </div>
                  </ContainerItemModal>
                  <ContainerItemModal>
                    <label>Responsável</label>
                    <div>
                      <span>{question.funcionario.nome}</span>
                    </div>
                  </ContainerItemModal>
                </S.RowItens>
                <S.RowItens>
                  <ContainerItemModal>
                    <label>Propriedade</label>
                    <div>
                      <span>{question.propriedade.nome_propriedade}</span>
                    </div>
                  </ContainerItemModal>
                  <ContainerItemModal>
                    <label>Produtor</label>
                    <div>
                      <span>{question.propriedade.nome_produtor}</span>
                    </div>
                  </ContainerItemModal>
                </S.RowItens>
                <S.RowItens>
                  <ContainerItemModal>
                    <label>Início</label>
                    <div>
                      <span>{formatISODate(question.data_inicio)}</span>
                    </div>
                  </ContainerItemModal>
                  <ContainerItemModal>
                    <label>Fim</label>
                    <div>
                      <span>{formatISODate(question.data_final)}</span>
                    </div>
                  </ContainerItemModal>
                </S.RowItens>
                <S.RowItens>
                  <ContainerItemModal>
                    <label>Status</label>
                    <div>
                      <span>{question.status}</span>
                    </div>
                  </ContainerItemModal>
                </S.RowItens>
                <S.RowItensLarge>
                  <ContainerItemModal>
                    <label>Recomendação Técnica</label>
                    <div>
                      <span>
                        {question.recomendacao_tecnica
                          ? question.recomendacao_tecnica
                          : '-'}
                      </span>
                    </div>
                  </ContainerItemModal>
                </S.RowItensLarge>
                {question.respostas
                  .filter(r => r.status !== 'C')
                  .map((resposta, index) => (
                    <Fragment key={index}>
                      <S.RowItens>
                        <h2>{resposta.questionario.descricao}</h2>
                      </S.RowItens>
                      <S.ContainerListPage>
                        <table>
                          <thead style={{ position: 'static' }}>
                            <tr>
                              <th className="first-table-th">Pergunta</th>
                              <th className="list-th">Respostas</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resposta.itens
                              .sort(
                                (a, b) => a.ordem_pergunta - b.ordem_pergunta,
                              )
                              .map((item, i) => (
                                <tr key={i}>
                                  <td className="list-td-small">
                                    {item.grau1_pergunta}.{item.grau2_pergunta}{' '}
                                    {item.texto_pergunta}{' '}
                                  </td>
                                  <td className="list-td-large">
                                    {getAnswerValue(item)}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </S.ContainerListPage>
                    </Fragment>
                  ))}
              </ModalInfoTemplete>
            )}
            {modalType === 'print' && (
              <ModalInfoTemplete
                option={'pqfl_visitas'}
                titleModal={
                  <S.TitleWrapper>
                    <span>Relatório de Visita PQFL</span>
                  </S.TitleWrapper>
                }
                closeModal={handleCloseModal}
                isOpen={openModal}
                headerAction={
                  <S.ActionButton onClick={handleOpenNewTab}>
                    📄 Abrir em Nova Aba
                  </S.ActionButton>
                }
              >
                <div style={{ width: '100%', height: '95vh' }}>
                  <PDFViewer
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  >
                    <PQFLDocument
                      base64Data={finalLogo}
                      assinatura_produtor={finalSignature}
                      assinatura_tecnico={finalTechSignature}
                      username={user?.DFLOGINUSUARIO}
                      company={{ legalName: question.unidade.nome }}
                      answers={question.respostas.filter(r => r.status !== 'C')}
                      qualityItems={qualityData}
                      property={{
                        name: question.propriedade.nome_propriedade,
                        producerName: question.propriedade.nome_produtor,
                        municipalityName: question.propriedade.nome_municipio,
                      }}
                      technicalRecommendation={question.recomendacao_tecnica}
                      endDate={question.data_final}
                    />
                  </PDFViewer>
                </div>
              </ModalInfoTemplete>
            )}
          </>
        )}
      </ContainerPage>
    </>
  );
};
